"use client";

import { Eye, EyeClosed } from "lucide-react";
import { useTranslations } from "next-intl";
import { useState } from "react";
import { toast, ToastContainer } from "react-toastify";
import { useParams, useRouter } from "next/navigation";
import { ToastTheme } from "@/utils/constants";
import SafeLink from "../SafeLink";
import { Locale } from "@/models/Locale";
import { goApiFetch } from "@/lib/goApiClient";

type SignInFormData = {
  email: string;
  password: string;
};

type ApiError = {
  error: string;
};

type ApiResponse = {
  message: string;
  data: {
    user_id: string;
    email: string;
    name: {
      first: string;
      last: string;
    };
    role?: "owner" | "admin";
    token: string;
    isEmailVerified: boolean;
  };
};

export function SignInForm() {
  const t = useTranslations("components.sign_in");
  const router = useRouter();
  const { locale } = useParams<{ locale: Locale }>();
  const [formData, setFormData] = useState<SignInFormData>({
    email: "",
    password: "",
  });

  const [errors, setErrors] = useState({
    email: "",
    password: "",
    general: "",
  });

  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const toggleShowPassword = () => setShowPassword(!showPassword);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateForm = (data: SignInFormData = formData): boolean => {
    const newErrors = {
      email: "",
      password: "",
      general: "",
    };

    const trimmedEmail = data.email.trim();

    if (!trimmedEmail) {
      newErrors.email = t("error_email_required");
    } else if (!validateEmail(trimmedEmail)) {
      newErrors.email = t("error_enter_invalid_email");
    }

    if (!data.password) {
      newErrors.password = t("error_password_required");
    }

    setErrors(newErrors);
    return !Object.values(newErrors).some((error) => error !== "");
  };

  const handleInputChange = (field: keyof SignInFormData, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user starts typing
    if (errors[field as keyof typeof errors]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const sanitizedEmail = formData.email.trim();
    const sanitizedData: SignInFormData = {
      ...formData,
      email: sanitizedEmail,
    };
    setFormData(sanitizedData);

    if (!validateForm(sanitizedData)) {
      return;
    }

    setIsLoading(true);

    try {
      const response = await goApiFetch("/v1/auth/signin", {
        method: "POST",
        body: {
          email: sanitizedEmail.toLowerCase(),
          password: formData.password,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        const errorData = data as ApiError;
        setErrors((prev) => ({
          ...prev,
          general: errorData.error || t("signin_failed"),
        }));
        toast.error(errorData.error || t("signin_failed"), ToastTheme);
        return;
      }

      const successData = data as ApiResponse;

      // Store token in localStorage and cookies for authentication
      if (typeof window !== "undefined") {
        localStorage.setItem("auth_token", successData.data.token);
        localStorage.setItem(
          "user_data",
          JSON.stringify({
            user_id: successData.data.user_id,
            email: successData.data.email,
            name: successData.data.name,
            role: successData.data.role,
            isEmailVerified: successData.data.isEmailVerified,
          }),
        );

        // Set token as cookie via API for middleware access
        fetch("/api/auth/set-token", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token: successData.data.token }),
        }).catch((err) => console.error("Failed to set token cookie:", err));
      }

      toast.success(t("signin_successful"), ToastTheme);

      // Check email verification status and redirect accordingly
      if (successData.data.isEmailVerified) {
        // Admins land in the admin area; everyone else goes to the business dashboard
        const destination =
          successData.data.role === "admin" ? "/admin" : "/dashboard";
        setTimeout(() => {
          router.push(destination);
        }, 700);
      } else {
        // Email is not verified, store email for verification page and redirect
        if (typeof window !== "undefined") {
          localStorage.setItem(
            "pending_verification_email",
            successData.data.email,
          );
        }
        setTimeout(() => {
          router.push("/verify-pending");
        }, 700);
      }
    } catch (error) {
      console.error("Sign in error:", error);
      const errorMessage = t("network_error");
      setErrors((prev) => ({
        ...prev,
        general: errorMessage,
      }));
      toast.error(errorMessage, ToastTheme);
    } finally {
      setIsLoading(false);
    }
  };

  const AlreadyHaveAccount = t.rich("have_not_account", {
    sign_up: (chunks) => (
      <SafeLink
        href="/sign-up"
        locale={locale}
        className="text-h-400 ms-1 font-semibold underline"
      >
        {chunks}
      </SafeLink>
    ),
  });

  const PrivacyCheckBoxText = t.rich("forgot_password", {
    renew_password: (chunks) => (
      <SafeLink
        href="/forgot-password"
        locale={locale}
        className="text-md font-semibold text-[#0C4974] not-italic underline"
        rel="noopener noreferrer"
      >
        {chunks}
      </SafeLink>
    ),
  });

  return (
    <div className="flex w-full grow flex-col items-start justify-center gap-8 rounded-lg p-8">
      <form
        onSubmit={handleSubmit}
        className="flex w-full grow flex-col items-start justify-center gap-8"
        aria-label="Sign In Form"
      >
        {/*Headers*/}
        <div className="w-full">
          <h2 className="text-ht-brown mb-2 text-3xl font-semibold">
            {t("title")}
          </h2>
          <p>{AlreadyHaveAccount}</p>
        </div>

        {/* General Error */}
        {errors.general && (
          <div className="w-full rounded-md border border-red-200 bg-red-50 p-3">
            <p className="text-sm text-red-700">{errors.general}</p>
          </div>
        )}

        {/*Inputs*/}
        <div className="w-full">
          <div className="mb-4">
            <label
              htmlFor="email"
              className="block font-semibold text-gray-600"
              aria-label="Email Address"
            >
              {t("label_email_title")}
            </label>
            <input
              type="email"
              id="email"
              value={formData.email}
              onChange={(e) => handleInputChange("email", e.target.value)}
              onBlur={() => handleInputChange("email", formData.email.trim())}
              required
              placeholder={t("placeholder_email")}
              className={`w-full rounded border-b border-b-[#E6997B] py-2 leading-tight focus:outline-none ${
                errors.email ? "border border-red-500" : ""
              } text-ht-brown text-sm font-semibold placeholder-gray-400`}
            />
            {errors.email && (
              <p className="text-xs text-red-500 italic">{errors.email}</p>
            )}
          </div>
          <div className="flex flex-col">
            <label
              htmlFor="password"
              className="block font-semibold text-gray-600"
              aria-label="Password"
            >
              {t("label_password_title")}
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                value={formData.password}
                onChange={(e) => handleInputChange("password", e.target.value)}
                required
                placeholder={t("placeholder_password")}
                className={`w-full rounded border-b border-b-[#E6997B] py-2 leading-tight focus:outline-none ${
                  errors.password ? "border border-red-500" : ""
                } text-ht-brown text-sm font-semibold placeholder-gray-400`}
              />
              <button
                type="button"
                onClick={toggleShowPassword}
                className="absolute end-0 top-2 bottom-0"
              >
                {showPassword ? (
                  <Eye className="h-5 w-5 text-gray-500" />
                ) : (
                  <EyeClosed className="h-5 w-5 text-gray-500" />
                )}
              </button>
            </div>
            {errors.password && (
              <p className="text-xs text-red-500 italic">{errors.password}</p>
            )}
            <div className="flex flex-row items-center justify-end gap-2 py-4">
              <p className="text-xs italic">{PrivacyCheckBoxText}</p>
            </div>
          </div>
        </div>
        <button
          type="submit"
          onClick={async (e) => {
            await handleSubmit(e);
          }}
          disabled={isLoading}
          className="focus:shadow-outline bg-h-500 hover:bg-h-600 disabled:bg-h-300 w-full max-w-44 cursor-pointer self-end rounded-md px-6 py-3 font-bold text-white transition-colors focus:outline-none disabled:cursor-not-allowed"
          aria-label="Sign In"
        >
          {isLoading ? t("signing_in") : t("title")}
        </button>
      </form>
      {/*Buttons*/}
      <div className="flex w-full flex-col items-end gap-4 md:items-center">
        {/* <button
          type="submit"
          onClick={async (e) => {
            await handleSubmit(e);
          }}
          disabled={isLoading}
          className="focus:shadow-outline bg-h-500 hover:bg-h-600 disabled:bg-h-300 w-full max-w-44 cursor-pointer rounded-md px-6 py-3 font-bold text-white transition-colors focus:outline-none disabled:cursor-not-allowed"
          aria-label="Sign In"
        >
          {isLoading ? t("signing_in") : t("title")}
        </button> */}
        {/* <div className="relative mt-4 flex w-full max-w-88 justify-center self-center text-sm text-gray-500 md:mt-0 md:self-auto">
          <span className="z-10 bg-white px-2 text-center">
            {t("or_login_w_with_google")}
          </span>
          <div className="absolute start-0 end-0 top-3 w-full border-t border-t-gray-500"></div>
        </div> */}
        {/* <button
          className="gsi-material-button self-center md:self-auto"
          aria-label="Sign in with Google"
          disabled={isLoading}
        >
          <div className="gsi-material-button-state"></div>
          <div className="gsi-material-button-content-wrapper">
            <div className="gsi-material-button-icon">
              <svg
                version="1.1"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 48 48"
                xmlnsXlink="http://www.w3.org/1999/xlink"
                style={{ display: "block" }}
              >
                <path
                  fill="#EA4335"
                  d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
                ></path>
                <path
                  fill="#4285F4"
                  d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
                ></path>
                <path
                  fill="#FBBC05"
                  d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
                ></path>
                <path
                  fill="#34A853"
                  d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
                ></path>
                <path fill="none" d="M0 0h48v48H0z"></path>
              </svg>
            </div>
            <span className="hidden">Sign in with Google</span>
          </div>
        </button>
        <form action={loginWithGithub}>
          <button
            type="submit"
            className="gsi-material-button self-center md:self-auto"
            aria-label="Sign in with Github"
            disabled={isLoading}
          ></button>
        </form> */}
      </div>
      <ToastContainer />
    </div>
  );
}
